import { useEffect, useRef } from "react";

interface PerformanceMetric {
  name: string;
  duration: number;
  timestamp: number;
}

class PerformanceMonitor {
  private metrics: PerformanceMetric[] = [];
  private static instance: PerformanceMonitor;

  static getInstance(): PerformanceMonitor {
    if (!PerformanceMonitor.instance) {
      PerformanceMonitor.instance = new PerformanceMonitor();
    }
    return PerformanceMonitor.instance;
  }

  mark(name: string): void {
    performance.mark(name);
  }

  measure(name: string, startMark: string, endMark?: string): number {
    const end = endMark || `${startMark}_end`;
    performance.mark(end);
    
    try {
      const measure = performance.measure(name, startMark, end);
      const metric = {
        name,
        duration: measure.duration,
        timestamp: Date.now(),
      };
      
      this.metrics.push(metric);
      
      // Keep only last 50 metrics
      if (this.metrics.length > 50) {
        this.metrics.shift();
      }
      
      // Log slow operations (>1000ms)
      if (measure.duration > 1000) {
        console.warn(`⚠️ Slow operation: ${name} took ${Math.round(measure.duration)}ms`);
      }
      
      return measure.duration;
    } catch (error) {
      console.error("Performance measurement failed:", error);
      return 0;
    }
  }

  getMetrics(): PerformanceMetric[] {
    return [...this.metrics];
  }

  getAverageTime(name: string): number {
    const filtered = this.metrics.filter(m => m.name === name);
    if (filtered.length === 0) return 0;
    
    const sum = filtered.reduce((acc, m) => acc + m.duration, 0);
    return sum / filtered.length;
  }

  clear(): void {
    this.metrics = [];
    performance.clearMarks();
    performance.clearMeasures();
  }
}

export function usePerformanceMonitor(componentName: string) {
  const monitor = PerformanceMonitor.getInstance();
  const startMarkRef = useRef(`${componentName}_start`);
  const mountTimeRef = useRef<number>(0);

  useEffect(() => {
    // Mark component mount
    monitor.mark(startMarkRef.current);
    
    return () => {
      // Measure component lifecycle on unmount
      const duration = monitor.measure(
        `${componentName}_lifecycle`,
        startMarkRef.current
      );
      mountTimeRef.current = duration;
    };
  }, [componentName, monitor]);

  const measureAsync = async <T,>(
    operationName: string,
    operation: () => Promise<T>
  ): Promise<T> => {
    const startMark = `${componentName}_${operationName}_start`;
    monitor.mark(startMark);
    
    try {
      const result = await operation();
      monitor.measure(`${componentName}_${operationName}`, startMark);
      return result;
    } catch (error) {
      monitor.measure(`${componentName}_${operationName}_error`, startMark);
      throw error;
    }
  };

  return {
    measureAsync,
    getMetrics: () => monitor.getMetrics(),
    getAverageTime: (name: string) => monitor.getAverageTime(name),
  };
}

// Hook to monitor page load performance
export function usePageLoadPerformance(pageName: string) {
  useEffect(() => {
    const monitor = PerformanceMonitor.getInstance();
    
    // Wait for page to be fully loaded
    if (document.readyState === 'complete') {
      measurePageLoad();
    } else {
      window.addEventListener('load', measurePageLoad);
      return () => window.removeEventListener('load', measurePageLoad);
    }

    function measurePageLoad() {
      const perfData = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
      
      if (perfData) {
        console.log(`📊 ${pageName} Performance:`, {
          'DNS Lookup': Math.round(perfData.domainLookupEnd - perfData.domainLookupStart),
          'TCP Connection': Math.round(perfData.connectEnd - perfData.connectStart),
          'Request Time': Math.round(perfData.responseEnd - perfData.requestStart),
          'DOM Processing': Math.round(perfData.domComplete - perfData.domInteractive),
          'Load Complete': Math.round(perfData.loadEventEnd - perfData.fetchStart),
        });
      }
    }
  }, [pageName]);
}
